import axios from 'axios';
import User from '../api/user';
import * as mutation from '../mutations/user';

const state = {
  idx: 0,
  token: '', // idToken, kakao access_token
  id: '',
  email: '',
  name: '',
  birthday: 0,
  social_type: '',
  profile_image: null,
  allow_mail: false,
  allow_push: false,
  bookmark_cnt: 0,
  subscribe_cnt: 0,
  phone: null,
  zipcode: null,
  address: null,
  detail_address: null,
  balance: 0,
  account_bank: '',
  account_number: 0,
  login_at: '',
  bookmarks_max_page: 2,
  bookmarks: [],
  wishes_max_page: 2,
  wish_cnt: 0,
  wishes: [],
};

// getters
const getters = {
  idx: state => state.idx,
  token: state => state.token,
  id: state => state.id,
  email: state => state.email,
  name: state => state.name,
  birthday: state => state.birthday,
  social_type: state => state.social_type,
  profile_image: state => state.profile_image,
  allow_mail: state => state.allow_mail,
  allow_push: state => state.allow_push,
  bookmark_cnt: state => state.bookmark_cnt,
  subscribe_cnt: state => state.subscribe_cnt,
  phone: state => state.phone,
  zipcode: state => state.zipcode,
  address: state => state.address,
  detail_address: state => state.detail_address,
  balance: state => state.balance,
  account_bank: state => state.account_bank,
  account_number: state => state.account_number,
  login_at: state => state.login_at,
  bookmarks: state => state.bookmarks,
  wishes: state => state.wishes,
  wish_cnt: state => state.wish_cnt,
};

// actions
const actions = {
  setUser ({ commit }, fields) { // 유저 정보를 내부 state 에 저장
    commit(mutation.SET_USER, fields);
  },
  setJWT ({ commit }, jwt) { // localstorage 에 jwt 저장, header 에 jwt 설정, native 에 jwt 전달
    window.localStorage.setItem('jwt', jwt);
    axios.defaults.headers.common['X-Token'] = jwt;
    if (window.native) window.native.setJWT(jwt);
    // eslint-disable-next-line
    if (typeof webkit !== 'undefined') webkit.messageHandlers.setJWT.postMessage(jwt);
    // console.log('setJWT: ', jwt);
    commit(mutation.SET_USER, { token: jwt });
  },
  loginWithToken (context, jwt) { // 토큰(헤더)으로 로그인
    // 유저 정보 중 jwt 에 포함된 id, idx, name, email, profile_image, allow_push, login_at 만 반환
    axios.defaults.headers.common['X-Token'] = jwt;
    return User.getUserToken();
  },
  signUser (context, fields) { // 가입 또는 로그인
    // 유저 정보 전체 반환
    return User.postUserSign(fields);
  },
  checkUserEmail (context, email) { // 이메일 주소 가입 여부 확인
    return User.getUserEmail(email);
  },
  updateUserProfileImage (context, form) { // 프로필 이미지 수정
    return User.patchUserProfileImage(form);
  },
  updateUser (context, fields) {
    // 유저 부가정보 수정
    // jwt 에 포함된 정보 (name, email, profile_image, allow_push) 변경시에는 토큰이 재발급되므로 주의
    return User.patchUser(fields);
  },
  selectCounter ({ commit }, second) { // 북마크, 구독 카운터
    return User.getUserCount(second).then(response => {
      commit(mutation.SELECT_COUNTER, response.data);
    }).catch(err => {
      console.error(err);
    });
  },
  // selectAddress ({ commit }) {
  //   return User.getUserAddress().then(response => {
  //     commit(mutation.SELECT_ADDRESS, response.data);
  //   }).catch(err => {
  //     console.error(err);
  //   });
  // },
  selectAddress () {
    return User.getDeliveryInfo();
  },
  createAddress (context, { name, email, phone, zipcode, address, detailAddress }) {
    return User.postDeliveryInfo(name, email, phone, zipcode, address, detailAddress);
  },
  selectBookmarks ({ commit }, { second, userIdx, page }) { // 게시글 북마크
    if (page <= state.bookmarks_max_page) {
      return User.getPostUserIdxBookmark(second, userIdx, page).then(response => {
        commit(mutation.SELECT_BOOKMARKS, response);
      }).catch(err => {
        console.error(err);
      });
    }
  },
  selectWishes ({ commit }, { second, userIdx, page }) { // 상품 찜 목록
    if (page <= state.wishes_max_page) {
      return User.getStoreUserIdxBookmark(second, userIdx, page).then(response => {
        commit(mutation.SELECT_WISHES, response);
      }).catch(err => {
        console.error(err);
      });
    }
  },
  signOutUser () { // Cloudfront 쿠키 삭제 (동영상 재생시 동시접속 방지)
    return User.getUserSignout();
  },
};

// mutations
const mutations = {
  [mutation.SET_USER] (state, user) {
    Object.assign(state, user);
  },
  [mutation.SELECT_COUNTER] (state, counter) {
    state.bookmark_cnt = counter.bookmark_cnt;
    state.subscribe_cnt = counter.subscribe_cnt;
  },
  // [mutation.SELECT_ADDRESS] (state, response) {
  //   state.phone = response.phone;
  //   state.zipcode = response.zipcode;
  //   state.address = response.address;
  //   state.detail_address = response.detail_address;
  // },
  [mutation.SELECT_BOOKMARKS] (state, response) {
    state.bookmark_cnt = response.total_count;
    state.bookmarks_max_page = response.max_page;
    if (response.req_page === 1) state.bookmarks = response.data;
    else state.bookmarks.push(...response.data);
  },
  [mutation.SELECT_WISHES] (state, response) {
    state.wish_cnt = response.total_count;
    state.wishes_max_page = response.max_page;
    if (response.req_page === 1) state.wishes = response.data;
    else state.wishes.push(...response.data);
  },
  [mutation.TOGGLE_LIKE_POST] (state, { idx, flag }) { // 북마크 글 목록에서 좋아요
    if (state.bookmarks.length) {
      let target = state.bookmarks.find(el => {
        return el.idx === idx;
      });
      if (typeof target !== 'undefined' && typeof flag === 'boolean') {
        target.likes = flag;
        flag ? target.like_cnt++ : target.like_cnt--;
      }
    }
  },
  [mutation.TOGGLE_BOOKMARK_POST] (state, { idx, flag }) { // 북마크 글 목록에서 북마크
    if (state.bookmarks.length) {
      let target = state.bookmarks.find(el => {
        return el.idx === idx;
      });
      if (typeof target !== 'undefined') {
        target.bookmarks = flag;
        flag ? target.bookmark_cnt++ : target.bookmark_cnt--;
      }
    }
  },
  [mutation.TOGGLE_BOOKMARK_PRODUCT] (state, { idx, flag }) { // 찜 목록에서 찜
    if (state.wishes.length) {
      let target = state.wishes.find(el => {
        return el.idx === idx;
      });
      if (typeof target !== 'undefined') {
        target.bookmarks = flag;
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
